import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ComboBox } from "@/components/base/select/combobox";
import { SelectItem } from "@/components/base/select/select-item";
import { useDebounce } from "@/hooks/use-debounce";
import { searchTokens, type TokenSummary } from "@/lib/tokens";

/** Položka nabídky — společný průnik toho, co vracejí zdroje tokenů. */
export interface TokenOption {
  tokenId: string;
  username: string;
  state: string | null;
}

/**
 * Výběr tokenu s našeptáváním. Hledá **server** a vrací jen prvních pár shod;
 * dřívější rozbalovací seznam vypisoval všechny tokeny, což při tisících tokenů
 * (zátěžové testy) znamenalo nepoužitelnou nabídku a stažení celého seznamu.
 *
 * Hodnotou pole je vždy text v poli, ne vybraná položka nabídky: Token ID jde
 * i opsat ručně (nabídka ukazuje jen prvních `limit` shod, takže na vypsání
 * konkrétního tokenu se nedá spoléhat) a vybraná hodnota tím pádem nikdy
 * „nezmizí", když ji další hledání nevrátí.
 */
export function TokenPicker({
  value,
  onChange,
  authServerType,
  activeOnly = false,
  limit = 20,
  placeholder,
  isDisabled,
  search,
}: {
  value: string;
  onChange: (tokenId: string) => void;
  /** Omezení na konektor; `null`/nevyplněno = tokeny všech konektorů (eTTS loopback). */
  authServerType?: string | null;
  activeOnly?: boolean;
  limit?: number;
  placeholder?: string;
  isDisabled?: boolean;
  /** Vlastní zdroj nabídky (např. KB CAAS — aktivní tokeny KB Klíče). */
  search?: (query: string) => Promise<TokenOption[]>;
}) {
  const { t } = useTranslation();

  // Text v poli je zdrojem pravdy; hledá se až po odmlce, ať se neposílá dotaz na každou klávesu.
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const query = useDebounce(text, 300);

  const options = useQuery({
    queryKey: ["token-picker", authServerType ?? "*", activeOnly, limit, query, !!search],
    queryFn: () =>
      search
        ? search(query)
        : (searchTokens({ query, authServerType, activeOnly, limit }) as Promise<TokenSummary[]>),
    staleTime: 10_000,
  });

  const items = (options.data ?? []).map((tk) => ({
    id: tk.tokenId,
    label: tk.tokenId,
    supportingText: [tk.username, tk.state].filter(Boolean).join(" · "),
  }));

  return (
    <ComboBox
      aria-label={t("tokens.col.tokenId")}
      placeholder={placeholder ?? t("tokens.filter.tokenIdPlaceholder")}
      shortcut={false}
      isDisabled={isDisabled}
      // Bez tohohle by react-aria při opuštění pole zahodilo text, který neodpovídá
      // žádné položce nabídky — tedy i ručně opsané Token ID.
      allowsCustomValue
      items={items}
      inputValue={text}
      onInputChange={(next) => {
        setText(next);
        onChange(next);
      }}
      onSelectionChange={(key) => {
        if (key != null) {
          setText(String(key));
          onChange(String(key));
        }
      }}
    >
      {(item) => (
        <SelectItem
          id={item.id}
          label={item.label}
          supportingText={item.supportingText}
          // Bez tohohle si react-aria odvodí textovou hodnotu položky z jejího obsahu, tedy
          // `label + " " + supportingText` (viz SelectItem) — po výběru z nabídky by se do pole
          // dostalo „AT775488-1 970912027 · ACTIVE" a jako Token ID by to odešlo i na server.
          textValue={String(item.id)}
        />
      )}
    </ComboBox>
  );
}
